import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Clock, ListChecks, MapPin, Sparkles } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { getObjectColors } from '../../../utils/objectColors';
import type { PlanningTask } from '../../../types/dashboard';
import type { TaskChecklistGroup } from '../../../store/useMyChecklistStore';
import { TaskChecklistPanel } from './TaskChecklistPanel';

type TaskState = 'done' | 'now' | 'next';

interface TaskRowProps {
  task: PlanningTask;
  state: TaskState;
  /** Checklist of this task for the shown day; omitted when the task has none. */
  checklist?: TaskChecklistGroup;
  /** ISO date the checklist belongs to — required together with `checklist`. */
  checklistDate?: string;
  checklistReadOnly?: boolean;
}

export function TaskRow({
  task,
  state,
  checklist,
  checklistDate,
  checklistReadOnly = false,
}: TaskRowProps) {
  const { t } = useTranslation('dashboard');
  const colors = getObjectColors(task.objectId ?? task.objectName ?? '');
  const openItems = (checklist?.items ?? []).filter((i) => !i.isCompleted).length;
  // Open by default while something is still unconfirmed — that is what needs attention.
  const [expanded, setExpanded] = useState(openItems > 0);

  const stateBadge =
    state === 'done'
      ? {
          icon: Check,
          label: t('upNext.done'),
          className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300',
        }
      : state === 'now'
      ? {
          icon: Sparkles,
          label: t('upNext.now'),
          className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
        }
      : {
          icon: Clock,
          label: t('upNext.next'),
          className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
        };

  const StateIcon = stateBadge.icon;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
        state === 'now'
          ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10 dark:border-amber-800'
          : 'border-border bg-surface dark:bg-slate-900 dark:border-slate-700'
      } ${state === 'done' ? 'opacity-60' : ''}`}
    >
      <div
        className="w-1 self-stretch rounded-full shrink-0"
        style={{ background: colors.color }}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-mono-num uppercase tracking-wider ${stateBadge.className}`}
          >
            <StateIcon className="w-3 h-3" aria-hidden />
            {stateBadge.label}
          </span>
          <span className="font-mono-num text-xs text-ink-muted dark:text-slate-400">
            {task.startTime?.substring(0, 5)} – {task.endTime?.substring(0, 5)}
          </span>
        </div>
        <div className="font-semibold text-sm text-ink dark:text-slate-50 truncate">
          {task.objectName}
        </div>
        {task.taskName && (
          <div className="flex items-center gap-1 text-xs text-ink-muted dark:text-slate-400 truncate mt-0.5">
            <MapPin className="w-3 h-3 shrink-0" aria-hidden />
            <span className="truncate">
              {task.serviceName ? `${task.serviceName} – ` : ''}
              {task.taskName}
            </span>
          </div>
        )}

        {checklist && checklistDate && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink dark:text-slate-400 dark:hover:text-slate-200"
            >
              {expanded ? (
                <ChevronDown className="w-3 h-3" aria-hidden />
              ) : (
                <ChevronRight className="w-3 h-3" aria-hidden />
              )}
              <ListChecks className="w-3 h-3" aria-hidden />
              {openItems > 0
                ? t('checklist.openCount', { count: openItems })
                : t('checklist.allDone')}
            </button>

            {expanded && (
              <TaskChecklistPanel
                date={checklistDate}
                group={checklist}
                readOnly={checklistReadOnly}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
